import type {
  BatchArgs,
  BatchResponse,
  Folder,
  IRecording,
  RecordingTypes,
  Sort,
  Workspace,
} from '@carboncopyco/types';
import { Debug, removeUndefinedValues } from '@carboncopyco/utils';
import type { EndpointDefinitions } from '@reduxjs/toolkit/dist/query/react';
import type { QueryKeys } from '@reduxjs/toolkit/src/query/core/apiState';
import { folderApi, workspaceApi } from '../';
import { handleBatchRequest } from '../utils';
import {
  queryDataUpdateHandler,
  workspaceAddAsset,
  workspaceRemoveAsset,
} from '../utils/queryDataUpdateHandler';
import { mainApi } from './mainApi';

Debug.extend('demostack-data:api:recordingApi');

export interface RevertRecordingArgs {
  id: IRecording['id'];
  revisionId: string;
  organizationId: string;
}

export interface RestoreRecordingArgs {
  id: IRecording['id'];
  organizationId: string;
}

export interface DeleteRecordingArgs {
  id: IRecording['id'];
  organizationId: string;
  folderId?: Folder['id'];
}

export interface DuplicateRecordingArgs {
  id: IRecording['id'];
  organizationId: string;
}

export interface CopyRecordingArgs {
  id: IRecording['id'];
  recordName?: IRecording['recordName'];
  targetWorkspaceId: Workspace['id'];
  organizationId: string;
  folderId?: Folder['id'];
}

export interface UpdateRecordingArgs {
  id: IRecording['id'];
  description?: IRecording['description'];
  recordName?: IRecording['recordName'];
  shim?: {
    css?: string;
    isActive?: boolean;
    js?: string;
  };
  status?: 'recording_finished';
  organizationId: string;
  folderId?: Folder['id'];
}

export interface SpliceRecordingArgs {
  id: string;
  demoId: string;
  demoRevisionId?: string;
  recordRevisionId?: string;
  organizationId: string;
  folderId?: string;
}

export interface CreateRecordingArgs {
  description?: IRecording['description'];
  firstUrl?: string;
  recordName?: IRecording['recordName'];
  recorderUuid?: string;
  shim?: { css?: string; isActive?: boolean; js?: string };
  organizationId: string;
}

export interface GetRecordingArgs {
  id: IRecording['id'];
  removeDeleted?: boolean;
  organizationId: string;
}

export interface GetRecordingsArgs extends BatchArgs<Sort> {
  organizationId: string;
  workspaces?: string;
  recordingType?: RecordingTypes | RecordingTypes[];
}

export interface MoveRecordingArgs {
  organizationId: string;
  id: IRecording['id'];
  targetFolderId?: Folder['id'];
  currentFolderId?: Folder['id'];
  workspaceId?: Workspace['id'];
}

const recordingApi = mainApi.injectEndpoints({
  endpoints: (builder) => ({
    getRecording: builder.query<IRecording, GetRecordingArgs>({
      query: ({ organizationId, id, removeDeleted = false }) => ({
        url: `/api/${organizationId}/record/${id}`,
        method: 'GET',
        params: { removeDeleted: String(removeDeleted) },
      }),
      providesTags: (_, __, { id }) => [{ type: 'Recording', id }],
    }),

    getRecordings: builder.query<BatchResponse<IRecording>, GetRecordingsArgs>({
      query: ({ organizationId, workspaces = 'all', recordingType, ...rest }) =>
        handleBatchRequest({
          ...rest,
          params: removeUndefinedValues({
            workspaces,
            recordingType,
          }),
          url: `/api/${organizationId}/records`,
        }),
      providesTags: ['Recordings'],
    }),

    getArchivedRecordings: builder.query<
      BatchResponse<IRecording>,
      GetRecordingsArgs
    >({
      query: ({ organizationId, workspaces = 'all', ...rest }) =>
        handleBatchRequest({
          ...rest,
          params: removeUndefinedValues({ workspaces }),
          url: `/api/${organizationId}/records/archived`,
        }),
      providesTags: ['ArchivedRecordings'],
    }),

    createRecording: builder.mutation<IRecording, CreateRecordingArgs>({
      query: ({
        organizationId,
        description,
        firstUrl,
        recordName,
        recorderUuid,
        shim,
      }) => ({
        url: `/api/${organizationId}/records/new`,
        method: 'PUT',
        body: {
          description,
          firstUrl,
          recordName,
          recorderUuid,
          shim,
        },
      }),
      async onQueryStarted(_, { dispatch, getState, queryFulfilled }) {
        const state = getState();
        await workspaceAddAsset({
          queryFulfilled,
          state,
          dispatch,
          argumentKey: 'workspaceId',
          entityType: 'record',
        });
      },
      invalidatesTags: (res) =>
        res
          ? [
              { type: 'Recordings' },
              { type: 'Recording', id: res.id },
              {
                type: 'WorkspaceAssets',
                id: res.workspaceId,
              },
              { type: 'FolderAssets', id: res.folderId },
            ]
          : [{ type: 'Recordings' }],
    }),

    updateRecording: builder.mutation<IRecording, UpdateRecordingArgs>({
      query: ({
        organizationId,
        id,
        description,
        recordName,
        status,
        folderId,
      }) => ({
        url: `/api/${organizationId}/record/${id}`,
        method: 'POST',
        body: {
          description,
          recordName,
          status,
          ...removeUndefinedValues({
            folderId,
          }),
        },
      }),
      onQueryStarted: async (
        { organizationId, id, ...patch },
        { dispatch, queryFulfilled, getState }
      ) => {
        const patchResult = dispatch(
          recordingApi.util.updateQueryData(
            'getRecording',
            { organizationId, id },
            (draft) => Object.assign(draft, patch)
          )
        );
        queryFulfilled.catch(patchResult.undo);
        const state = getState();
        await workspaceAddAsset({
          queryFulfilled,
          state,
          dispatch,
          argumentKey: 'workspaceId',
          entityType: 'record',
        });
      },
      invalidatesTags: (res, __, { id, folderId }) =>
        res
          ? [
              { type: 'Recordings' },
              { type: 'Recording', id },
              { type: 'Revisions', id },
              {
                type: 'WorkspaceAssets',
                id: res.workspaceId,
              },
              { type: 'FolderAssets', id: folderId },
            ]
          : [
              { type: 'Recordings' },
              { type: 'Recording', id },
              { type: 'Revisions', id },
              { type: 'FolderAssets', id: folderId },
            ],
    }),

    deleteRecording: builder.mutation<IRecording, DeleteRecordingArgs>({
      query: ({ organizationId, id }) => ({
        url: `/api/${organizationId}/record/${id}`,
        method: 'DELETE',
      }),
      onQueryStarted: async (
        { folderId = '0', ...arg },
        { dispatch, getState, queryFulfilled }
      ) => {
        try {
          const { data: deletedRecording } = await queryFulfilled;
          const isDeletingFromFolder = String(folderId) !== '0';
          const state = getState();
          queryDataUpdateHandler({
            api: isDeletingFromFolder ? folderApi : workspaceApi,
            state,
            dispatch,
            endpointName: isDeletingFromFolder
              ? ('getFolderAssets' as QueryKeys<EndpointDefinitions>)
              : ('getWorkspaceAssets' as QueryKeys<EndpointDefinitions>),
            endpointArgumentKey: isDeletingFromFolder
              ? 'folderId'
              : 'workspaceId',
            endpointArgumentValue: isDeletingFromFolder
              ? folderId
              : deletedRecording.workspaceId,
            updateQueryDataFunction: (assets) =>
              workspaceRemoveAsset(assets, arg),
          });
        } catch (e) {
          console.error(e);
        }
      },
      invalidatesTags: (res, __, { id, folderId }) =>
        res
          ? [
              { type: 'Recording', id },
              { type: 'Recordings' },
              { type: 'ArchivedRecordings' },
              {
                type: 'WorkspaceAssets',
                id: res.workspaceId,
              },
              { type: 'FolderAssets', id: folderId },
            ]
          : [
              { type: 'Recording', id },
              { type: 'Recordings' },
              { type: 'ArchivedRecordings' },
            ],
    }),

    duplicateRecording: builder.mutation<IRecording, DuplicateRecordingArgs>({
      query: ({ organizationId, id }) => ({
        url: `/api/${organizationId}/record/${id}/duplicate`,
        method: 'PUT',
      }),
      async onQueryStarted(_, { dispatch, getState, queryFulfilled }) {
        const state = getState();
        await workspaceAddAsset({
          queryFulfilled,
          state,
          dispatch,
          argumentKey: 'workspaceId',
          entityType: 'record',
        });
      },
      invalidatesTags: (res) =>
        res
          ? [
              { type: 'Recordings' },
              {
                type: 'WorkspaceAssets',
                id: res.workspaceId,
              },
              { type: 'FolderAssets', id: res.folderId },
            ]
          : [{ type: 'Recordings' }],
    }),

    copyRecording: builder.mutation<IRecording, CopyRecordingArgs>({
      query: ({
        organizationId,
        id,
        recordName,
        targetWorkspaceId,
        folderId,
      }) => ({
        url: `/api/${organizationId}/record/${id}/copy`,
        method: 'PUT',
        body: { recordName, targetWorkspaceId, folderId },
      }),
      invalidatesTags: (res, __, { id }) =>
        res
          ? [
              { type: 'Recordings' },
              {
                type: 'WorkspaceAssets',
                id: res.workspaceId,
              },
              { type: 'FolderAssets', id: res.folderId },
              { type: 'Folders', id: res.workspaceId },
              { type: 'Folder', id: res.folderId },
              { type: 'Recording', id: res.id },
            ]
          : [{ type: 'Recordings' }, { type: 'Recording', id }],
    }),

    moveRecording: builder.mutation<IRecording, MoveRecordingArgs>({
      query: ({ organizationId, id, targetFolderId }) => ({
        url: `/api/${organizationId}/record/${id}/move`,
        method: 'PUT',
        body: removeUndefinedValues({
          folderId: targetFolderId,
        }),
      }),
      onQueryStarted: async (arg, { dispatch, getState, queryFulfilled }) => {
        try {
          const isMovingFromWorkspaceToFolder =
            arg.targetFolderId !== '0' && arg.currentFolderId === '0';
          await queryFulfilled;
          const state = getState();
          queryDataUpdateHandler({
            api: isMovingFromWorkspaceToFolder ? workspaceApi : folderApi,
            state,
            dispatch,
            endpointName: isMovingFromWorkspaceToFolder
              ? ('getWorkspaceAssets' as QueryKeys<EndpointDefinitions>)
              : ('getFolderAssets' as QueryKeys<EndpointDefinitions>),
            endpointArgumentKey: isMovingFromWorkspaceToFolder
              ? 'workspaceId'
              : 'folderId',
            endpointArgumentValue: isMovingFromWorkspaceToFolder
              ? arg.workspaceId
              : arg.currentFolderId,
            updateQueryDataFunction: (assets) =>
              workspaceRemoveAsset(assets, arg),
          });
        } catch (error) {
          console.error(error);
        }
      },
      invalidatesTags: (res) => [
        { type: 'Recording' },
        { type: 'WorkspaceAssets' },
        { type: 'FolderAssets' },
        { type: 'Folders', id: res.workspaceId },
      ],
    }),

    restoreRecording: builder.mutation<IRecording, RestoreRecordingArgs>({
      query: ({ organizationId, id }) => ({
        url: `/api/${organizationId}/record/${id}/restore`,
        method: 'POST',
      }),
      onQueryStarted: async (arg, { dispatch, getState, queryFulfilled }) => {
        try {
          const { data: restoredRecording } = await queryFulfilled;
          const state = getState();
          queryDataUpdateHandler({
            state,
            dispatch,
            endpointName:
              'getWorkspaceAssets' as QueryKeys<EndpointDefinitions>,
            endpointArgumentKey: 'workspaceId',
            endpointArgumentValue: restoredRecording.workspaceId,
            updateQueryDataFunction: (workspaceAssets) =>
              workspaceRemoveAsset(workspaceAssets, arg),
          });
        } catch (e) {
          console.error(e);
        }
      },
      invalidatesTags: (res, __, { id }) =>
        res
          ? [
              { type: 'ArchivedRecordings' },
              { type: 'Recording', id },
              { type: 'Recordings' },
              {
                type: 'WorkspaceAssets',
                id: res.workspaceId,
              },
              { type: 'Folders', id: res.folderId },
            ]
          : [
              { type: 'ArchivedRecordings' },
              { type: 'Recording', id },
              { type: 'Recordings' },
            ],
    }),

    revertRecording: builder.mutation<IRecording, RevertRecordingArgs>({
      query: ({ organizationId, id, revisionId }) => ({
        url: `/api/${organizationId}/record/${id}/revert`,
        method: 'PUT',
        body: { revisionId },
      }),
      invalidatesTags: (_, __, { id }) => [
        { type: 'Recordings' },
        { type: 'Recording', id },
        { type: 'Revisions', id },
      ],
    }),

    resetRecording: builder.mutation<unknown, { id: string }>({
      queryFn: () => {
        return { data: null };
      },
      invalidatesTags: (_, __, { id }) => [
        { type: 'Recordings' },
        { type: 'ArchivedRecordings' },
        { type: 'Recording', id },
        { type: 'Revisions', id },
      ],
    }),

    spliceRecording: builder.mutation<any, SpliceRecordingArgs>({
      query: ({
        organizationId,
        id,
        demoId,
        demoRevisionId,
        recordRevisionId,
        folderId,
      }) => ({
        url: `/api/${organizationId}/record/${id}/splice`,
        method: 'POST',
        body: removeUndefinedValues({
          demoId,
          demoRevisionId,
          recordRevisionId,
          folderId,
        }),
      }),
      invalidatesTags: (res) =>
        res
          ? [
              {
                type: 'WorkspaceAssets',
                id: res.workspaceId,
              },
              { type: 'FolderAssets', id: res.folderId },
            ]
          : [{ type: 'Recording' }],
    }),
  }),
});

export const {
  useGetRecordingQuery,
  useLazyGetRecordingQuery,
  useGetRecordingsQuery,
  useGetArchivedRecordingsQuery,
  useLazyGetArchivedRecordingsQuery,
  useLazyGetRecordingsQuery,
  useCreateRecordingMutation,
  useUpdateRecordingMutation,
  useDeleteRecordingMutation,
  useDuplicateRecordingMutation,
  useCopyRecordingMutation,
  useRestoreRecordingMutation,
  useRevertRecordingMutation,
  useSpliceRecordingMutation,
  useResetRecordingMutation,
  useMoveRecordingMutation,
} = recordingApi;
